if (!customElements.get('jp-post-delivery-time')) {
  customElements.define(
    'jp-post-delivery-time',
    class JpPostDeliveryTime extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.prefectureSelect = this.querySelector('#jp-post-delivery-prefecture');
        this.dateSelect = this.querySelector('#jp-post-delivery-date');
        this.timeSelect = this.querySelector('#jp-post-delivery-select');
        this.status = this.querySelector('.jp-post-delivery__status');
        this.values = { prefecture: '', date: '', time: '' };
        this.deliveryOptions = [];
        this.lookupController = null;
        this.isRequired = this.dataset.required === 'true';

        this.prefectureSelect?.addEventListener(
          'change',
          this.handlePrefectureChange.bind(this)
        );
        this.dateSelect?.addEventListener('change', this.handleDateChange.bind(this));
        this.timeSelect?.addEventListener('change', this.handleTimeChange.bind(this));

        this.loadSavedValues();
        this.validateCheckoutButtons();
      }

      getCheckoutButtons() {
        return [
          document.getElementById('checkout'),
          document.getElementById('CartDrawer-Checkout')
        ].filter(Boolean);
      }

      validateCheckoutButtons() {
        if (!this.isRequired) return;

        const isComplete =
          Boolean(this.values.prefecture) &&
          Boolean(this.values.date) &&
          Boolean(this.values.time);

        this.getCheckoutButtons().forEach((button) => {
          if (isComplete) {
            if (button.classList.contains('jp-post-delivery__disabled')) {
              button.disabled = false;
            }
            button.classList.remove('jp-post-delivery__disabled');
            button.removeAttribute('title');
          } else {
            button.disabled = true;
            button.classList.add('jp-post-delivery__disabled');
            button.setAttribute('title', this.dataset.messageCheckoutDisabled);
          }
        });
      }

      async loadSavedValues() {
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Failed to load cart');

          const cart = await response.json();
          const attributes = cart.attributes || {};
          const prefecture = attributes.delivery_prefecture || '';
          const savedDate = attributes.delivery_date || '';
          const savedTime = attributes.delivery_time || '';
          const hasValidPrefecture = this.hasPrefectureOption(prefecture);

          this.values = {
            prefecture: hasValidPrefecture ? prefecture : '',
            date: '',
            time: ''
          };
          if (this.prefectureSelect) {
            this.prefectureSelect.value = this.values.prefecture;
          }

          if (attributes.delivery_postcode) {
            await this.updateCartAttributes({ delivery_postcode: '' });
          }

          if (hasValidPrefecture) {
            await this.lookupPrefecture(prefecture, {
              savedDate,
              savedTime,
              shouldPersist: false
            });
          }
        } catch (error) {
          console.error('Error loading saved delivery preferences:', error);
          this.showStatus(this.dataset.messageLookupFailed, 'error');
        } finally {
          this.validateCheckoutButtons();
        }
      }

      async handlePrefectureChange(event) {
        const prefecture = event.target.value;
        this.values = { prefecture, date: '', time: '' };
        this.deliveryOptions = [];
        this.resetDateAndTimeSelects();
        this.showStatus('', '');
        this.validateCheckoutButtons();

        if (prefecture) {
          await this.lookupPrefecture(prefecture, { shouldPersist: true });
        } else {
          await this.updateCartAttributes({
            delivery_prefecture: '',
            delivery_postcode: '',
            delivery_date: '',
            delivery_time: ''
          });
        }
      }

      async lookupPrefecture(
        prefecture,
        { savedDate = '', savedTime = '', shouldPersist = true } = {}
      ) {
        this.lookupController?.abort();
        this.lookupController = new AbortController();
        this.classList.add('jp-post-delivery--loading');
        this.showStatus(this.dataset.messageLookingUp, 'loading');

        try {
          const endpoint = new URL(this.dataset.optionsEndpoint);
          endpoint.searchParams.set('prefecture', prefecture);
          const response = await fetch(endpoint.toString(), {
            signal: this.lookupController.signal
          });
          const body = await response.json();
          if (!response.ok) throw new Error(body.error || 'Lookup failed');

          if (this.prefectureSelect?.value !== prefecture) return;

          this.deliveryOptions = body.data.dates || [];
          this.values.prefecture = body.data.prefecture;
          this.populateDateSelect(savedDate);

          if (savedDate && this.deliveryOptions.some((option) => option.date === savedDate)) {
            this.values.date = savedDate;
            this.dateSelect.value = savedDate;
            this.applyTimeAvailability(savedDate, savedTime);
          }

          const message = (this.dataset.messageEligible || '')
            .replace('{prefecture}', body.data.prefecture);
          this.showStatus(message, 'success');

          if (shouldPersist) {
            await this.updateCartAttributes({
              delivery_prefecture: this.values.prefecture,
              delivery_postcode: '',
              delivery_date: this.values.date,
              delivery_time: this.values.time
            });
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Error loading delivery options:', error);
          this.deliveryOptions = [];
          this.values.date = '';
          this.values.time = '';
          this.resetDateAndTimeSelects();
          this.showStatus(this.dataset.messageLookupFailed, 'error');
        } finally {
          this.classList.remove('jp-post-delivery--loading');
          this.validateCheckoutButtons();
        }
      }

      populateDateSelect(preferredDate = '') {
        if (!this.dateSelect) return;

        const placeholder = this.dateSelect.options[0];
        this.dateSelect.replaceChildren(placeholder);

        this.deliveryOptions.forEach((option, index) => {
          const dateOption = document.createElement('option');
          dateOption.value = option.date;
          dateOption.textContent = this.formatDate(option.date, index === 0);
          this.dateSelect.appendChild(dateOption);
        });

        this.dateSelect.disabled = this.deliveryOptions.length === 0;
        this.dateSelect.value = this.deliveryOptions.some((option) => option.date === preferredDate)
          ? preferredDate
          : '';
      }

      applyTimeAvailability(date, preferredTime = '') {
        if (!this.timeSelect) return;

        const selectedDate = this.deliveryOptions.find((option) => option.date === date);
        const availableTimes = new Set(
          (selectedDate?.timeOptions || []).map((time) => this.normalizeTime(time))
        );

        this.timeSelect.querySelectorAll('[data-time-option]').forEach((option) => {
          const isAvailable = availableTimes.has(option.value);
          option.disabled = !isAvailable;
          option.hidden = !isAvailable;
        });

        const normalizedPreferredTime = this.normalizeTime(preferredTime);
        this.values.time = availableTimes.has(normalizedPreferredTime)
          ? normalizedPreferredTime
          : '';
        this.timeSelect.value = this.values.time;
        this.timeSelect.disabled = !selectedDate;
      }

      async handleDateChange(event) {
        this.values.date = event.target.value;
        this.values.time = '';
        this.applyTimeAvailability(this.values.date);
        this.validateCheckoutButtons();

        await this.updateCartAttributes({
          delivery_date: this.values.date,
          delivery_time: ''
        });
      }

      async handleTimeChange(event) {
        this.values.time = event.target.value;
        this.validateCheckoutButtons();
        await this.updateCartAttributes({ delivery_time: this.values.time });
      }

      async updateCartAttributes(attributes) {
        this.classList.add('jp-post-delivery--saving');
        try {
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes })
          });
          if (!response.ok) throw new Error('Failed to update cart');

          const cart = await response.json();
          this.dispatchEvent(new CustomEvent('delivery-preferences-updated', {
            detail: { values: { ...this.values }, cart },
            bubbles: true
          }));
          this.classList.add('jp-post-delivery--success');
          setTimeout(() => this.classList.remove('jp-post-delivery--success'), 1200);
          return cart;
        } catch (error) {
          console.error('Error updating delivery preferences:', error);
          this.showStatus(this.dataset.messageSavingFailed, 'error');
          this.classList.add('jp-post-delivery--error');
          setTimeout(() => this.classList.remove('jp-post-delivery--error'), 2000);
          return null;
        } finally {
          this.classList.remove('jp-post-delivery--saving');
          this.validateCheckoutButtons();
        }
      }

      resetDateAndTimeSelects() {
        if (this.dateSelect) {
          const placeholder = this.dateSelect.options[0];
          this.dateSelect.replaceChildren(placeholder);
          this.dateSelect.value = '';
          this.dateSelect.disabled = true;
        }
        if (this.timeSelect) {
          this.timeSelect.value = '';
          this.timeSelect.disabled = true;
          this.timeSelect.querySelectorAll('[data-time-option]').forEach((option) => {
            option.disabled = false;
            option.hidden = false;
          });
        }
      }

      hasPrefectureOption(value) {
        return Array.from(this.prefectureSelect?.options || [])
          .some((option) => option.value === value);
      }

      normalizeTime(value) {
        return String(value).replace(/\s/g, '');
      }

      formatDate(value, isEarliest) {
        const date = new Date(`${value}T00:00:00.000Z`);
        const locale = document.documentElement.lang || 'en';
        const formatted = new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          weekday: 'short',
          timeZone: 'UTC'
        }).format(date);
        return isEarliest
          ? `${formatted} (${this.dataset.messageEarliest})`
          : formatted;
      }

      showStatus(message, state) {
        if (!this.status) return;
        this.status.textContent = message || '';
        this.status.dataset.state = state || '';
      }
    }
  );
}

document.addEventListener('cart:updated', (event) => {
  document.querySelectorAll('jp-post-delivery-time').forEach((component) => {
    if (!event.detail?.cart) return;
    component.validateCheckoutButtons();
  });
});
